import React, { useState, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import { useAuth } from '@/hooks/useAuth';
import { useS3Manager } from '@/hooks/useS3Manager';
import { useKendraSync } from '@/hooks/useKendraSync';
import { getFolderBasePath } from '@/config/app-config';
import FolderTree from '@/components/s3-manager/FolderTree';
import FileList from '@/components/s3-manager/FileList';
import FileUploader from '@/components/s3-manager/FileUploader';
import KendraSyncPanel from '@/components/s3-manager/KendraSyncPanel';
import FilePreview from '@/components/s3-manager/FilePreview';
import { getErrorMessage } from '@/utils/error';

/**
 * S3管理メインページ
 */
const S3ManagerPage: React.FC = () => {
  const { accessibleFolders, loading: authLoading } = useAuth();

  // 現在選択中のフォルダ
  const [currentPrefix, setCurrentPrefix] = useState<string>('');
  const [currentRootLabel, setCurrentRootLabel] = useState<string>('');

  // S3管理フック
  const {
    objects,
    folders,
    isLoading,
    error,
    selectedKeys,
    setSelectedKeys,
    uploadProgresses,
    isUploading,
    handleUploadFiles,
    clearUploadProgresses,
    handleCreateFolder,
    handleDeleteObjects,
    handleMoveObject,
    handleRenameObject,
    handleGetDownloadUrl,
    isDeleting,
    isMoving,
    refresh,
    refreshKey,
  } = useS3Manager({ currentPrefix });

  // Kendra同期フック
  const kendraSyncProps = useKendraSync({});

  // オブジェクト数・フォルダ数・ファイル数の計算
  const fileCount = React.useMemo(
    () => objects.filter(o => !o.isFolder).length,
    [objects]
  );
  const folderCount = folders.length;
  const totalCount = folderCount + fileCount;

  // 現在フォルダ直下のファイル名一覧（上書き判定用）
  const existingFileNames = React.useMemo(
    () => objects.filter(o => !o.isFolder).map(o => o.name),
    [objects]
  );

  // ダイアログ状態
  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveSourceKeys, setMoveSourceKeys] = useState<string[]>([]);
  const [selectedDestFolder, setSelectedDestFolder] = useState<string | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameSourceKey, setRenameSourceKey] = useState<string | null>(null);
  const [newObjectName, setNewObjectName] = useState('');

  // 通知
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });

  // フォルダ選択
  const handleFolderSelect = useCallback((folderPath: string, label: string) => {
    setCurrentPrefix(folderPath);
    if (folderPath.split('/').filter(Boolean).length <= 3) {
      setCurrentRootLabel(label);
    }
    setSelectedKeys([]);
  }, [setSelectedKeys]);

  // ルートに戻る
  const handleNavigateToRoot = useCallback(() => {
    if (accessibleFolders.length > 0) {
      const rootFolder = accessibleFolders.find(f =>
        currentPrefix.startsWith(getFolderBasePath(f.value))
      );
      if (rootFolder) {
        setCurrentPrefix(getFolderBasePath(rootFolder.value));
      }
    }
    setSelectedKeys([]);
  }, [accessibleFolders, currentPrefix, setSelectedKeys]);

  // パンくずナビゲーション
  const handleNavigateToBreadcrumb = useCallback((path: string) => {
    setCurrentPrefix(path);
    setSelectedKeys([]);
  }, [setSelectedKeys]);

  // フォルダクリック
  const handleFolderClick = useCallback((folderPath: string) => {
    setCurrentPrefix(folderPath);
    setSelectedKeys([]);
  }, [setSelectedKeys]);

  // フォルダ作成
  const handleCreateFolderSubmit = async () => {
    if (!newFolderName.trim()) return;

    try {
      await handleCreateFolder(newFolderName.trim());
      setSnackbar({ open: true, message: 'フォルダを作成しました', severity: 'success' });
      setCreateFolderDialogOpen(false);
      setNewFolderName('');
    } catch (err: unknown) {
      setSnackbar({ open: true, message: getErrorMessage(err, 'フォルダの作成に失敗しました'), severity: 'error' });
    }
  };

  // ファイルアップロード
  const handleUpload = async (files: File[]) => {
    try {
      await handleUploadFiles(files);
      setSnackbar({ open: true, message: 'アップロードが完了しました', severity: 'success' });
    } catch (err: unknown) {
      setSnackbar({ open: true, message: getErrorMessage(err, 'アップロードに失敗しました'), severity: 'error' });
    }
  };

  // 削除
  const handleDelete = async () => {
    try {
      await handleDeleteObjects(selectedKeys);
      setSnackbar({ open: true, message: '削除しました', severity: 'success' });
      setDeleteDialogOpen(false);
    } catch (err: unknown) {
      setSnackbar({ open: true, message: getErrorMessage(err, '削除に失敗しました'), severity: 'error' });
    }
  };

  // 単一アイテム削除（コンテキストメニューから）
  const handleDeleteSingle = (keys: string[]) => {
    setSelectedKeys(keys);
    setDeleteDialogOpen(true);
  };

  // ダウンロード
  const handleDownload = async (key: string) => {
    try {
      const url = await handleGetDownloadUrl(key);
      const fileName = key.split('/').filter(Boolean).pop() || 'download';

      try {
        // Blob 経由でダウンロード（社内プロキシによる日本語URLナビゲーション遮断を回避し、正しいファイル名で保存）
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } catch (fetchErr) {
        console.warn('Blob download failed, falling back to window.open:', fetchErr);
        // フォールバック: 直接URLを開く
        window.open(url, '_blank');
      }
    } catch (err: unknown) {
      setSnackbar({ open: true, message: getErrorMessage(err, 'ダウンロードに失敗しました'), severity: 'error' });
    }
  };


  // 移動ダイアログを開く
  const handleMoveStart = (sourceKey: string) => {
    setMoveSourceKeys([sourceKey]);
    setSelectedDestFolder(null);
    setMoveDialogOpen(true);
  };

  // 一括移動ダイアログを開く
  const handleBatchMoveStart = () => {
    setMoveSourceKeys(selectedKeys);
    setSelectedDestFolder(null);
    setMoveDialogOpen(true);
  };

  // 移動実行
  const handleMoveExecute = async (destinationPrefix: string) => {
    if (moveSourceKeys.length === 0) return;

    try {
      await Promise.all(
        moveSourceKeys.map(async (key) => {
          await handleMoveObject(key, destinationPrefix);
        })
      );
      setSnackbar({ open: true, message: `${moveSourceKeys.length}件のアイテムを移動しました`, severity: 'success' });
      setMoveDialogOpen(false);
      setMoveSourceKeys([]);
      setSelectedKeys([]);
    } catch (err: unknown) {
      setSnackbar({ open: true, message: getErrorMessage(err, '一部または全てのアイテムの移動に失敗しました'), severity: 'error' });
      refresh();
    }
  };

  // リネームダイアログを開く
  const handleRenameStart = (sourceKey: string) => {
    setRenameSourceKey(sourceKey);
    const parts = sourceKey.split('/').filter(Boolean);
    const currentName = parts.pop() || '';
    setNewObjectName(currentName);
    setRenameDialogOpen(true);
  };

  // リネーム実行
  const handleRenameExecute = async () => {
    if (!renameSourceKey || !newObjectName.trim()) return;

    const trimmedName = newObjectName.trim();
    if (trimmedName.includes('/') || trimmedName.includes('\\')) {
      setSnackbar({ open: true, message: '名前変更時に「/」や「\\」は使用できません', severity: 'warning' });
      return;
    }

    try {
      await handleRenameObject(renameSourceKey, trimmedName);
      setSnackbar({ open: true, message: '名前を変更しました', severity: 'success' });
      setRenameDialogOpen(false);
      setRenameSourceKey(null);
      setNewObjectName('');
    } catch (err: unknown) {
      setSnackbar({ open: true, message: getErrorMessage(err, '名前の変更に失敗しました'), severity: 'error' });
    }
  };

  // Kendra同期
  const handleSyncKendra = async () => {
    const success = await kendraSyncProps.handleStartSync();
    if (success) {
      setSnackbar({ open: true, message: 'Kendra同期を開始しました', severity: 'info' });
    }
  };

  // 初回フォルダ選択
  React.useEffect(() => {
    if (!authLoading && accessibleFolders.length > 0 && !currentPrefix) {
      const firstFolder = accessibleFolders[0];
      setCurrentPrefix(getFolderBasePath(firstFolder.value));
      setCurrentRootLabel(firstFolder.label);
    }
  }, [authLoading, accessibleFolders, currentPrefix]);

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }

  if (accessibleFolders.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          アクセス可能なフォルダがありません。管理者にお問い合わせください。
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3} lg={2}>
          <FolderTree
            accessibleFolders={accessibleFolders}
            selectedFolder={currentPrefix}
            onFolderSelect={handleFolderSelect}
            subFolders={folders}
            currentPrefix={currentPrefix}
            refreshTrigger={refreshKey}
          />
        </Grid>

        {/* メインエリア: ファイル一覧 */}
        <Grid item xs={12} md={6} lg={7}>
          <Paper sx={{ p: 2 }}>
            {/* ヘッダー: オブジェクト数・内訳情報 */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1.5,
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600, fontSize: '1.15rem' }}>
                  オブジェクト {isLoading ? '' : `(${totalCount})`}
                </Typography>
                {!isLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Chip
                      label={`フォルダ: ${folderCount}件`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.75rem', height: 24 }}
                    />
                    <Chip
                      label={`ファイル: ${fileCount}件`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.75rem', height: 24 }}
                    />
                    {selectedKeys.length > 0 && (
                      <Chip
                        label={`${selectedKeys.length}件選択中`}
                        size="small"
                        color="primary"
                        sx={{ fontSize: '0.75rem', height: 24, fontWeight: 'bold' }}
                      />
                    )}
                  </Box>
                )}
              </Box>
              <Tooltip title="更新">
                <IconButton onClick={refresh} size="small" aria-label="一覧を更新">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* ツールバー */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Tooltip title="フォルダ作成">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CreateNewFolderIcon />}
                    onClick={() => setCreateFolderDialogOpen(true)}
                    disabled={!currentPrefix}
                  >
                    フォルダ作成
                  </Button>
                </Tooltip>
                <Tooltip title="選択したアイテムを移動">
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DriveFileMoveIcon />}
                      onClick={handleBatchMoveStart}
                      disabled={selectedKeys.length === 0 || isMoving}
                    >
                      移動 ({selectedKeys.length})
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="選択したアイテムを削除">
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => setDeleteDialogOpen(true)}
                      disabled={selectedKeys.length === 0 || isDeleting}
                    >
                      削除 ({selectedKeys.length})
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            {/* エラー表示 */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* ファイル一覧 */}
            <FileList
              objects={objects}
              folders={folders}
              currentPrefix={currentPrefix}
              rootLabel={currentRootLabel}
              selectedKeys={selectedKeys}
              isLoading={isLoading}
              syncErrors={kendraSyncProps.syncErrors}
              onSelectKeys={setSelectedKeys}
              onFolderClick={handleFolderClick}
              onNavigateToRoot={handleNavigateToRoot}
              onNavigateToBreadcrumb={handleNavigateToBreadcrumb}
              onDelete={handleDeleteSingle}
              onDownload={handleDownload}
              onPreview={setPreviewKey}
              onMove={handleMoveStart}
              onRename={handleRenameStart}
            />
          </Paper>
        </Grid>

        {/* 右サイドバー: アップロード・同期 */}
        <Grid item xs={12} md={3} lg={3}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FileUploader
              onUpload={handleUpload}
              isUploading={isUploading}
              uploadProgresses={uploadProgresses}
              onClearProgresses={clearUploadProgresses}
              disabled={!currentPrefix}
              existingFileNames={existingFileNames}
            />

            <KendraSyncPanel
              syncStatus={kendraSyncProps.syncStatus}
              isSyncing={kendraSyncProps.isSyncing}
              isLoadingStatus={kendraSyncProps.isLoadingStatus}
              error={kendraSyncProps.error}
              statusText={kendraSyncProps.statusText}
              statusColor={kendraSyncProps.statusColor}
              onStartSync={handleSyncKendra}
            />
          </Box>
        </Grid>
      </Grid>

      {/* フォルダ作成ダイアログ */}
      <Dialog open={createFolderDialogOpen} onClose={() => setCreateFolderDialogOpen(false)}>
        <DialogTitle>フォルダ作成</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="フォルダ名"
            fullWidth
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleCreateFolderSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFolderDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleCreateFolderSubmit} variant="contained">
            作成
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>削除確認</DialogTitle>
        <DialogContent>
          <Typography>
            {selectedKeys.length}件のアイテムを削除します。この操作は取り消せません。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 移動ダイアログ */}
      <Dialog open={moveDialogOpen} onClose={() => setMoveDialogOpen(false)}>
        <DialogTitle>移動先を選択 ({moveSourceKeys.length}件のアイテム)</DialogTitle>
        <DialogContent sx={{ minWidth: 350, minHeight: 250, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            移動先のフォルダをクリックして選択し、「移動」ボタンを押してください。
          </Typography>
          <Box sx={{ flexGrow: 1, border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
            <FolderTree
              accessibleFolders={accessibleFolders}
              selectedFolder={selectedDestFolder}
              onFolderSelect={(path) => setSelectedDestFolder(path)}
              refreshTrigger={refreshKey}
              excludedPrefixes={moveSourceKeys.filter(k => k.endsWith('/'))}
            />
          </Box>
          {selectedDestFolder && (
            <Typography variant="caption" color="primary" sx={{ mt: 1, wordBreak: 'break-all' }}>
              選択中: {selectedDestFolder}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveDialogOpen(false)}>キャンセル</Button>
          <Button 
            onClick={() => selectedDestFolder && handleMoveExecute(selectedDestFolder)}
            color="primary" 
            variant="contained" 
            disabled={!selectedDestFolder}
          >
            移動
          </Button>
        </DialogActions>
      </Dialog>

      {/* ファイルプレビュー */}
      <FilePreview
        open={!!previewKey}
        fileKey={previewKey || ''}
        onClose={() => setPreviewKey(null)}
        onDownload={handleDownload}
      />

      {/* 名前変更ダイアログ */}
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)}>
        <DialogTitle>名前の変更</DialogTitle>
        <DialogContent sx={{ minWidth: 300 }}>
          <TextField
            autoFocus
            margin="dense"
            label="新しい名前"
            fullWidth
            value={newObjectName}
            onChange={(e) => setNewObjectName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleRenameExecute();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleRenameExecute} variant="contained">
            変更
          </Button>
        </DialogActions>
      </Dialog>

      {/* 通知 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default S3ManagerPage;
